<template>
  <v-container>
    <ScheduleViewOptions v-if="adminView" />
    <div v-if="adminView && numHiddenEvents > 0">
      <v-checkbox v-model="showHiddenEvents" :label="`Show hidden events? (${numHiddenEvents})`"></v-checkbox>
    </div>
    <v-btn v-if="adminView" class="schedule__export primary" @click="exportCsv">Export to CSV <v-icon right>mdi-export</v-icon></v-btn>
    <template v-if="ambassadorView">
      <h1>
        Ambassador: {{ targetAmbassadorData.firstName }} {{ targetAmbassadorData.lastName }}

        <div>
          <v-btn v-if="!mergedView" color="primary" @click="mergedView = true">
            Merge Schedules
            <v-icon right>mdi-call-merge</v-icon>
          </v-btn>
          <v-btn v-if="mergedView" color="primary" @click="mergedView = false">
            Split Schedules
            <v-icon right>mdi-call-split</v-icon>
          </v-btn>
        </div>
      </h1>
    </template>
    <div v-if="authorSchedules && authorSchedules.length" class="all-schedules__wrapper">
      <template v-if="mergedView">
        <ScheduleSingle
          :authors="mergedAuthorData"
          :events="mergedEvents"
          :eventHeaders="headers"
          :ambassador="targetAmbassadorData"
          :formattedDateRange="formattedDateRange"
          :mergedView="true"
        />
      </template>
      <template v-else>
        <div
          v-for="(schedule, i) in authorSchedules"
          :key="`schedule-${i}`"
          class="all-schedules__single-wrapper"
          :style="i < authorSchedules.length - 1 ? 'page-break-after: always' : ''"
        >
          <ScheduleSingle
            :targetAuthorData="schedule.authorData"
            :events="schedule.scheduleData"
            :eventHeaders="eventHeaders(adminView, schedule.authorData, userRole === 'admin')"
            :ambassador="schedule.ambassadorData"
            :formattedDateRange="formattedDateRange"
            :rsvpTotals="rsvpTotals"
            :rsvpAuthorsByEventId="rsvpAuthorsByEventId"
          />
        </div>
      </template>
    </div>
    <!-- Original -->
    <ScheduleSingle
      v-else-if="targetAuthorData || !$route.query.author"
      :targetAuthorData="targetAuthorData"
      :events="filteredEvents"
      :eventHeaders="headers"
      :ambassador="myAmbassador"
      :formattedDateRange="formattedDateRange"
      :rsvpTotals="rsvpTotals"
      :rsvpAuthorsByEventId="rsvpAuthorsByEventId"
    />
    <div v-else-if="$route.query.author && !targetAuthorData && authorQueryComplete" class="author-not-found">
      <p>No author found for ID: {{ $route.query.author }}</p>
      <p class="bold">Please ensure that the author ID in the URL is correct and try again.</p>
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import ScheduleViewOptions from '@/components/ScheduleViewOptions'
import ScheduleSingle from '@/components/ScheduleSingle'
import { escapeCsvField } from '@/assets/scripts/helpers'

import { httpsCallable } from 'firebase/functions'

export default {
  name: 'Schedule',
  components: {
    ScheduleViewOptions,
    ScheduleSingle
  },
  async created () {
    const authorId = this.$route.query.author
    if (authorId) {
      console.log('Loading events for author', authorId)
      this.authorQueryComplete = false
      try {
        this.targetAuthorData = await this.getTargetAuthor(authorId)
      } catch (err) {
        console.log(err)
      } finally {
        this.authorQueryComplete = true
      }
    } else {
      this.authorQueryComplete = true
    }

    const ambassadorId = this.$route.query.ambassador
    if (ambassadorId) {
      console.log('Loading events for ambassador', ambassadorId)
      try {
        this.targetAmbassadorData = await this.getTargetAmbassador(ambassadorId)
      } catch (err) {
        console.log(err)
      }
      if (this.targetAmbassadorData?.authors) {
        console.log('Num authors:', this.targetAmbassadorData.authors.length)
        for (let i = 0; i < this.targetAmbassadorData.authors.length; i++) {
          const targetId = this.targetAmbassadorData.authors[i]
          console.log('Target Author ID', targetId)
          const res = await httpsCallable(this.fbFunctions, 'getMyEvents')({ authorId: targetId })
          const authorData = await this.getTargetAuthor(targetId)
          const amRes = await httpsCallable(this.fbFunctions, 'getMyAmbassador')({ authorId: targetId })
          this.authorSchedules.push({
            authorData: authorData,
            scheduleData: res.data,
            ambassadorData: amRes.data
          })
          // Format / expand event data as necessary
          res.data.forEach((e) => {
            e.author = authorData
          })
          this.mergedEvents = res.data.concat(this.mergedEvents)
          this.mergedAuthorData.push(authorData)
        }
        console.log('Merged Authors:', this.mergedAuthorData)
        console.log('Merged Events:', this.mergedEvents)
      }
    }
    await this.globalFlagsListener()
    console.log('My Events', this.myEvents)
  },
  async mounted () {
    setTimeout(async () => {
      await this.authorsListener()
    }, 0)
  },
  data: () => ({
    targetAuthorData: null,
    targetAmbassadorData: null,
    authorSchedules: [], // For displaying multiple schedules at once (for an ambassador)
    mergedView: false,
    mergedAuthorData: [],
    mergedEvents: [],
    showHiddenEvents: false,
    authorQueryComplete: false
  }),
  computed: {
    ...mapState(['firestore', 'globalSettings', 'fbFunctions']),
    ...mapGetters([
      'userRole',
      'myEvents',
      'numHiddenEvents',
      'myAmbassador',
      'eventHeaders',
      'formattedDateRange',
      'locationById',
      'authorById',
      'eventCategoryById'
    ]),
    filteredEvents () {
      // If in global view, only show events for which hideOnGlobalSchedule is not set / false
      if (this.myEvents && this.globalView && !this.showHiddenEvents) {
        return this.myEvents.filter((event) => !event.hideOnGlobalSchedule)
      } else {
        return this.myEvents
      }
    },
    adminView () {
      return this.userRole === 'admin' || this.userRole === 'viewer'
    },
    ambassadorView () {
      return !!this.targetAmbassadorData
    },
    // If we're not viewing an ambassador or author
    globalView () {
      return !this.targetAuthorData && !this.targetAmbassadorData
    },
    headers () {
      return this.eventHeaders(this.adminView, this.targetAuthorData, this.userRole === 'admin')
    },
    rsvpTotals () {
      // Derive from rsvpAuthorsByEventId so the number always matches the list (names + +1s)
      const totals = {}
      const authorsMap = this.rsvpAuthorsByEventId
      if (!authorsMap) return totals
      Object.keys(authorsMap).forEach((eventId) => {
        const authors = authorsMap[eventId]
        totals[eventId] = authors.reduce((sum, a) => sum + 1 + (a.hasPlusOne ? 1 : 0), 0)
      })
      return totals
    },
    /** Map event id -> array of { authorId, hasPlusOne } for authors who RSVPed yes. */
    rsvpAuthorsByEventId () {
      const map = {}
      if (!this.myEvents) return map
      this.myEvents.forEach((event) => {
        const byId = {} // authorId -> { authorId, hasPlusOne }
        if (event.rsvps) {
          for (const [authorId, value] of Object.entries(event.rsvps)) {
            if (value === 'yes') {
              byId[authorId] = {
                authorId,
                hasPlusOne: !!(event.plusOneRsvps && event.plusOneRsvps[authorId] === 'yes')
              }
            }
          }
        }
        if (event.plusOneRsvps) {
          for (const [authorId, value] of Object.entries(event.plusOneRsvps)) {
            if (value === 'yes' && !byId[authorId]) {
              byId[authorId] = { authorId, hasPlusOne: true }
            } else if (value === 'yes' && byId[authorId]) {
              byId[authorId].hasPlusOne = true
            }
          }
        }
        const list = Object.values(byId)
        if (list.length) map[event.id] = list
      })
      return map
    }
  },
  methods: {
    ...mapActions([
      'getTargetAuthor',
      'getTargetAmbassador',
      'globalFlagsListener',
      'authorsListener'
    ]),
    formatTagName (tag) {
      // If tag is >= 20 characters and matches an author ID, return author's fullName
      // Otherwise return tag in uppercase
      if (tag && tag.length >= 20 && this.authorById(tag)) {
        return this.authorById(tag).fullName
      }
      return tag ? tag.toUpperCase() : ''
    },
    formatDateOnly (date) {
      // Format as "M/D/YYYY"
      // date is ISO format (e.g., "2026-03-13")
      if (!date) return ''
      // Parse date string directly to avoid timezone issues
      // Date is in YYYY-MM-DD format
      const dateParts = date.split('-')
      if (dateParts.length !== 3) return ''
      const year = parseInt(dateParts[0], 10)
      const month = parseInt(dateParts[1], 10)
      const day = parseInt(dateParts[2], 10)
      return `${month}/${day}/${year}`
    },
    formatDateTime (date, time) {
      // Format as "M/D/YYYY HH:mm:ss"
      // date is ISO format (e.g., "2026-03-13")
      // time is HH:mm format (e.g., "17:00")
      if (!date || !time) return ''
      // Parse date string directly to avoid timezone issues
      // Date is in YYYY-MM-DD format
      const dateParts = date.split('-')
      if (dateParts.length !== 3) return ''
      const year = parseInt(dateParts[0], 10)
      const month = parseInt(dateParts[1], 10)
      const day = parseInt(dateParts[2], 10)
      // Ensure time has seconds (add :00 if not present)
      const timeWithSeconds = time.includes(':') && time.split(':').length === 2
        ? time + ':00'
        : time
      return `${month}/${day}/${year} ${timeWithSeconds}`
    },
    exportCsv () {
      console.log('Exporting Schedule CSV...')

      // Get all events to export (use filteredEvents to respect showHiddenEvents)
      const eventsToExport = [...this.filteredEvents]

      // Sort by date and time
      eventsToExport.sort((a, b) => {
        // First sort by date
        if (a.date !== b.date) {
          return a.date.localeCompare(b.date)
        }
        // Then by start time
        if (a.start && b.start) {
          return a.start.localeCompare(b.start)
        }
        // Events without start time go to the end
        if (!a.start && b.start) return 1
        if (a.start && !b.start) return -1
        return 0
      })

      // Build CSV header
      let csvData = 'Date,Time,Start Time,End Time,Title,Description,Location,Tags,RSVP Tags,Categories\n'

      // Add each event as a row
      eventsToExport.forEach((event) => {
        // Date (formatted as M/D/YYYY to match Start/End Time format)
        const date = event.date ? escapeCsvField(this.formatDateOnly(event.date)) : ''

        // Time
        let time = 'TBD'
        if (event.start && event.end && !event.dateTimeTbd) {
          time = escapeCsvField(
            this.militaryToAmPm(event.start, false) + ' - ' + this.militaryToAmPm(event.end)
          )
        }

        // Start Time (formatted as M/D/YYYY HH:mm:ss)
        const startTime = (event.date && event.start && !event.dateTimeTbd)
          ? escapeCsvField(this.formatDateTime(event.date, event.start))
          : ''

        // End Time (formatted as M/D/YYYY HH:mm:ss)
        const endTime = (event.date && event.end && !event.dateTimeTbd)
          ? escapeCsvField(this.formatDateTime(event.date, event.end))
          : ''

        // Event Description
        const eventDescription = event.description
          ? escapeCsvField(event.description)
          : ''

        // Session Description
        const sessionDescription = event.sessionDescription
          ? escapeCsvField(event.sessionDescription)
          : ''

        // Location
        const location = event.location && this.locationById(event.location)
          ? escapeCsvField(this.locationById(event.location))
          : ''

        // Tags (convert IDs to names, join with commas, no line breaks)
        const tagNames = []
        if (event.tags && Array.isArray(event.tags)) {
          event.tags.forEach(tag => {
            const tagName = this.formatTagName(tag)
            if (tagName) tagNames.push(tagName.trim())
          })
        }
        const tags = escapeCsvField(tagNames.filter(t => t).join(', '))

        // RSVP Tags (convert IDs to names, join with commas, no line breaks)
        const rsvpTagNames = []
        if (event.rsvpTags && Array.isArray(event.rsvpTags)) {
          event.rsvpTags.forEach(tag => {
            const tagName = this.formatTagName(tag)
            if (tagName) rsvpTagNames.push(tagName.trim())
          })
        }
        const rsvpTags = escapeCsvField(rsvpTagNames.filter(t => t).join(', '))

        // Categories (convert IDs to names, join with commas, no line breaks)
        const categoryNames = []
        if (event.categoryIds && Array.isArray(event.categoryIds)) {
          event.categoryIds.forEach(categoryId => {
            const category = this.eventCategoryById(categoryId)
            if (category) categoryNames.push(category.name.trim())
          })
        }
        const categories = escapeCsvField(categoryNames.filter(c => c).join(', '))

        // Build CSV row
        csvData += `"${date}","${time}","${startTime}","${endTime}","${eventDescription}","${sessionDescription}","${location}","${tags}","${rsvpTags}","${categories}"\n`
      })

      // Create and download the file
      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0] + 'UTC'
      link.download = `schedule_export_${timestamp}.csv`
      link.click()

      // Clean up
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style lang="scss">
.author-not-found {
  margin-top: 20px;
  p {
    margin-bottom: 0;
  }
  .bold {
    font-weight: bold;
  }
}

.schedule__export {
  margin-top: 12px;
  margin-bottom: 12px;

  @media print {
    display: none;
  }
}
</style>
